/**
 * Average spents aggregated memoized Selectors
 * All datas are computed from customers counts and revenues fetched data
 *
 */
import { createSelector } from '@reduxjs/toolkit';

import {
  areCustomersCountsAggregatedLoading,
  getCustomersCountsAggregatedError,
  getReferenceCustomersCountsAggregated,
  getReferenceDataKpiOfSelectedStores as getReferenceCustomersCountsDataOfSelectedStores,
  getSelectedCustomersCountsAggregated,
  getSelectedDataKpiOfSelectedStores as getSelectedCustomersCountsDataOfSelectedStores,
} from '../CustomersCountsAggregated/selectors';
import { getEvolution } from '../helpers';
import {
  areRevenuesAggregatedLoading,
  getReferenceDataKpiOfSelectedStores as getReferenceRevenuesDataOfSelectedStores,
  getReferenceRevenuesAggregated,
  getRevenuesAggregatedError,
  getSelectedDataKpiOfSelectedStores as getSelectedRevenuesDataOfSelectedStores,
  getSelectedRevenuesAggregated,
} from '../RevenuesAggregated/selectors';

/**
 * If last attempt at fetching data resulted in an error, returns the error value
 *
 * @return {string}
 */
export const getAverageSpentsAggregatedError = createSelector(
  [getCustomersCountsAggregatedError, getRevenuesAggregatedError],
  (customersCountsError, revenuesError) => customersCountsError || revenuesError,
);

/**
 * If data is being fetched
 *
 * @return {boolean}
 */
export const areAverageSpentsAggregatedLoading = createSelector(
  [areCustomersCountsAggregatedLoading, areRevenuesAggregatedLoading],
  (customersCountsLoading, revenuesLoading) => customersCountsLoading || revenuesLoading,
);

/**
 * Get aggregated average spents data of selected stores and selected period
 * If there is no data, the api does not return the `selected` key, so we check the existence of the key
 *
 * @return {Array}
 */
export const getSelectedDataKpiOfSelectedStores = createSelector(
  [getSelectedCustomersCountsDataOfSelectedStores, getSelectedRevenuesDataOfSelectedStores],
  (customersCounts, revenues) =>
    customersCounts.length && revenues.length
      ? customersCounts.map(({ id, value }, i) => ({
          ...(value.customersCount
            ? {
                value: {
                  tax_excluded: revenues[i].value.tax_excluded / value.customersCount,
                  tax_included: revenues[i].value.tax_included / value.customersCount,
                },
              }
            : {
                value: {
                  tax_excluded: 0,
                  tax_included: 0,
                },
              }),
          id,
        }))
      : [],
);

/**
 * Provides aggregated average spents on selected stores and selected period
 *
 * @return {number}
 */
export const getSelectedAverageSpentsAggregated = createSelector(
  [getSelectedCustomersCountsAggregated, getSelectedRevenuesAggregated],
  (customersCounts, revenues) => (customersCounts ? revenues / customersCounts : revenues),
);

/**
 * Get aggregated average spents data of selected stores and reference period
 * If there is no data, the api does not return the `reference` key, so we check the existence of the key
 *
 * @return {Array}
 */
export const getReferenceDataKpiOfSelectedStores = createSelector(
  [getReferenceCustomersCountsDataOfSelectedStores, getReferenceRevenuesDataOfSelectedStores],
  (customersCounts, revenues) =>
    customersCounts.length && revenues.length
      ? customersCounts.map(({ id, value }, i) => ({
          ...(value.customersCount
            ? {
                value: {
                  tax_excluded: revenues[i].value.tax_excluded / value.customersCount,
                  tax_included: revenues[i].value.tax_included / value.customersCount,
                },
              }
            : {
                value: {
                  tax_excluded: 0,
                  tax_included: 0,
                },
              }),
          id,
        }))
      : [],
);

/**
 * Provides average spent on selected stores and reference period
 *
 * @return {number}
 */
export const getReferenceAverageSpentsAggregated = createSelector(
  [getReferenceCustomersCountsAggregated, getReferenceRevenuesAggregated],
  (customersCounts, revenues) => (customersCounts ? revenues / customersCounts : 0),
);

/**
 * Get evolution of average spents from datas of reference period and selected period
 * Ratio between reference and selected datas
 *
 * @return {number}
 */
export const getEvolutionAverageSpentsAggregated = createSelector(
  [getReferenceAverageSpentsAggregated, getSelectedAverageSpentsAggregated],
  (reference, selected) => getEvolution(selected, reference),
);
